import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { BarChartWidget, LineChartWidget, generateMockData } from '../components/Charts';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function Billing() {
  const { tenantId } = useParams();
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState(50);
  const [paymentMethod, setPaymentMethod] = useState('stripe');

  const fetchBillingData = async (withLoading = true) => {
    try {
      const headers = tenantId ? { 'x-tenant-id': tenantId } : {};
      const [balanceRes, txRes] = await Promise.all([
        api.get('/billing/balance', { headers }),
        api.get('/billing/transactions', { headers })
      ]);
      setBalance(balanceRes.data.data);
      setTransactions(txRes.data.data.transactions || []);
      if (withLoading) setLoading(false);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    }
  };

  useEffect(() => {
    fetchBillingData(true);
  }, [tenantId]);

  // Polling for real-time data updates
  useEffect(() => {
    const pollBilling = setInterval(() => {
      fetchBillingData(false);
    }, 15000); // 15 seconds

    return () => {
      clearInterval(pollBilling);
    };
  }, [tenantId]);

  const paymentMethods = [
    { id: 'stripe', name: 'Credit/Debit Card', icon: '💳' },
    { id: 'paypal', name: 'PayPal', icon: '🅿️' },
    { id: 'phonepe', name: 'PhonePe', icon: '📱' },
    { id: 'razorpay', name: 'UPI / Razorpay', icon: '₹' }
  ];

  const handleRecharge = async (e) => {
    e.preventDefault();
    try {
      if (paymentMethod === 'stripe') {
        const { data } = await api.post(`/billing/pay/stripe/create-intent`, 
          { amount },
          { headers: { 'x-tenant-id': tenantId } }
        );
        alert('Stripe: Use test card 4242424242424242');
      } else if (paymentMethod === 'paypal') {
        await api.post(`/billing/pay/paypal/create-order`,
          { amount },
          { headers: { 'x-tenant-id': tenantId } }
        );
        alert('PayPal: Redirect to PayPal checkout');
      } else if (paymentMethod === 'phonepe') {
        await api.post(`/billing/pay/phonepe/create`,
          { amount, tenantId },
          { headers: { 'x-tenant-id': tenantId } }
        );
        alert('PhonePe: Open PhonePe app');
      } else if (paymentMethod === 'razorpay') {
        await api.post(`/billing/pay/razorpay/create-order`,
          { amount },
          { headers: { 'x-tenant-id': tenantId } }
        );
        alert('Razorpay: Open UPI payment');
      }
      setShowModal(false);
      setAmount(50);
      alert('Payment initiated! Backend will credit after confirmation.');
    } catch (error) {
      alert('Recharge failed');
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Billing & Credits</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          + Add Credits
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-6 rounded shadow">
          <div className="text-gray-500">Available Balance</div>
          <div className="text-3xl font-bold text-green-600">
            ${balance?.available?.toFixed(2) || '0.00'}
          </div>
        </div>
        <div className="bg-white p-6 rounded shadow">
          <div className="text-gray-500">Total Spent</div>
          <div className="text-3xl font-bold text-red-600">
            ${balance?.spent?.toFixed(2) || '0.00'}
          </div>
        </div>
        <div className="bg-white p-6 rounded shadow">
          <div className="text-gray-500">Auto-Recharge</div>
          <div className="text-lg">
            {balance?.autoRecharge?.enabled ? (
              <span className="text-green-600">Enabled (${balance.autoRecharge.amount})</span>
            ) : (
              <span className="text-gray-500">Disabled</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <BarChartWidget title="Spending Over Time" data={generateMockData(14)} xKey="date" yKey="revenue" />
        <LineChartWidget title="Usage vs Cost" data={generateMockData(14)} xKey="date" yKeys={['apiCalls', 'revenue']} />
      </div>

      <div className="bg-white rounded shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Description</th>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr><td colSpan="4" className="px-4 py-4 text-center">No transactions yet</td></tr>
            ) : (
              transactions.map(tx => (
                <tr key={tx.transactionId} className="border-t">
                  <td className="px-4 py-3">{new Date(tx.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-3">{tx.description || tx.type}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-sm ${
                      tx.type === 'credit' ? 'bg-green-100 text-green-800' :
                      tx.type === 'debit' ? 'bg-red-100 text-red-800' : 'bg-gray-100'
                    }`}>
                      {tx.type}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-right ${
                    tx.amount > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount?.toFixed(2)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
          <div className="bg-white p-6 rounded-lg w-full max-w-md my-6">
            <h2 className="text-xl font-bold mb-4">Add Credits</h2>
            <form onSubmit={handleRecharge}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Payment Method</label>
                <div className="grid grid-cols-2 gap-2">
                  {paymentMethods.map((pm) => (
                    <button
                      key={pm.id}
                      type="button"
                      onClick={() => setPaymentMethod(pm.id)}
                      className={`p-3 border rounded text-left ${
                        paymentMethod === pm.id ? 'border-indigo-600 bg-indigo-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-lg mr-2">{pm.icon}</span>
                      <span className="text-sm">{pm.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Amount (INR)</label>
                <select
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value={500}>₹500 - Intro Pack</option>
                  <option value={5000}>₹5,000 - Basic Pack</option>
                  <option value={10000}>₹10,000 - Bronze Pack</option>
                  <option value={50000}>₹50,000 - Silver Pack</option>
                  <option value={100000}>₹100,000 - Gold Pack</option>
                  <option value={1000000}>₹1,000,000 - Platinum Pack</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded">Pay ₹{amount}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}